import { addDoc, collection, doc, getCountFromServer, getDocs, limit, query, QueryDocumentSnapshot, runTransaction, setDoc, SnapshotOptions, where, WithFieldValue } from "firebase/firestore";
import { db } from "../firebase";
import { firebaseDataConverter } from "./Converters/FirebaseConverter";
import { Voucher, VoucherFromDbMapper, VoucherToDbMapper } from "../models/Voucher";
import { VoucherDb } from "./models/VoucherDb";
import { v4 as uuidv4 } from 'uuid';

class VoucherService {
    private static instance: VoucherService | null = null;

    private static tableName: string = "voucher";
    public static getInstance(): VoucherService {
        if (this.instance === null) {
            this.instance = new VoucherService();
        }
        return this.instance;
    }

    public async getVouchersAsync(): Promise<Voucher[]> {
        const documents = await getDocs(query(collection(db, VoucherService.tableName)).withConverter<VoucherDb>(firebaseDataConverter))
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return VoucherFromDbMapper(data);
        });
        return mapped;
    }

    public async getVouchersByCompanyAndProductAsync(companyGuid: string, productGuid: string): Promise<Voucher[]> {
        const q = query(collection(db, VoucherService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid)).withConverter<VoucherDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return VoucherFromDbMapper(data);
        });
        return mapped;
    }

    public async getVoucherCountByCompanyAndProductAsync(companyGuid: string, productGuid: string): Promise<number> {
        const q = query(collection(db, VoucherService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid)).withConverter<VoucherDb>(firebaseDataConverter);
        const count = await getCountFromServer(q)
        return count.data().count;
    }

    public async getUnassignedVouchersAsync(companyGuid: string, productGuid: string): Promise<Voucher[]> {
        const q = query(collection(db, VoucherService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("redeembedDate", "==", null)).withConverter<VoucherDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return VoucherFromDbMapper(data);
        });
        return mapped;
    }

    public async getUnassignedVoucherCountAsync(companyGuid: string, productGuid: string): Promise<number> {
        const q = query(collection(db, VoucherService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("redeembedDate", "==", null)).withConverter<VoucherDb>(firebaseDataConverter);
        const count = await getCountFromServer(q)
        return count.data().count;
    }

    public async getUnexportedVouchersAsync(companyGuid: string, productGuid: string): Promise<Voucher[]> {
        const q = query(collection(db, VoucherService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("exportedDate", "==", null),
            where("redeembedDate", "==", null)).withConverter<VoucherDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        const mapped = documents.docs.map(doc => {
            var data = doc.data()
            return VoucherFromDbMapper(data);
        });
        return mapped;
    }

    public async getUnexportedVoucherCountAsync(companyGuid: string, productGuid: string): Promise<number> {
        const q = query(collection(db, VoucherService.tableName),
            where("companyGuid", "==", companyGuid),
            where("productGuid", "==", productGuid),
            where("exportedDate", "==", null),
            where("redeembedDate", "==", null)).withConverter<VoucherDb>(firebaseDataConverter);
        const count = await getCountFromServer(q)
        return count.data().count;
    }

    public async getVoucherByKeyAsync(key: string): Promise<Voucher | null> {
        const q = query(collection(db, VoucherService.tableName),
            where("key", "==", key),
            limit(1)).withConverter<VoucherDb>(firebaseDataConverter);
        const documents = await getDocs(q)
        if (documents.docs.length < 1) return null;
        return VoucherFromDbMapper(documents.docs[0].data());
    }

    public async addVoucherAsync(voucher: Voucher): Promise<void> {
        var mappedDb = VoucherToDbMapper(voucher);
        await setDoc(doc(db, VoucherService.tableName, uuidv4()).withConverter<VoucherDb>(firebaseDataConverter), mappedDb);
    }

    public async addVoucherBatchAsync(vouchers: Voucher[]): Promise<void> {
        await runTransaction(db, async (transaction) => {
            vouchers.forEach((x) => {
                var mappedDb = VoucherToDbMapper(x);
                let documentReference = doc(db, VoucherService.tableName, x.guid).withConverter(firebaseDataConverter);

                transaction.set(documentReference, mappedDb);
            });
        });
    }

    public async updateVouchersAsync(vouchers: Voucher[]): Promise<void> {
        await runTransaction(db, async (transaction) => {
            vouchers.forEach(async (x) => {
                var mappedDb = VoucherToDbMapper(x);
                let documentReference = doc(db, VoucherService.tableName, x.guid).withConverter(firebaseDataConverter);
                transaction.update(documentReference, mappedDb);
            });
        });
    }
}

const useVoucherService = () => {
    return VoucherService.getInstance();
}

export default useVoucherService;

